import { useMemo, useState } from 'react';
import { usePhoneCrumb } from '../lib/usePhoneCrumb';
import {
  Card, Chip, DataTable, EmptyState, FilterRow, Modal, Pane, PaneHead, type TableRow,
} from '@/components/kit';
import { useShellChrome } from '@/layouts/shellChrome';
import { usePageMeta } from '@/hooks/usePageMeta';
import { useAuthStore } from '@/stores/useAuthStore';
import type { AuditLogEntry } from '@/services/api/auditLogsApi';
import { shortName, stamp, ymd } from '../lib/scope';
import { useAuditLog, useProviders, type AuditQuery } from '../lib/useManagerData';
import { exportCsv } from '@/pages/roles/lib/exportCsv';
import { ManagerSurface } from '../components/ManagerSurface';
import { ManagerMenu } from '../components/ManagerMenu';

type ChipKind = 'warn' | 'soft' | 'success' | 'outline';

/**
 * THE FAMILIES, AND WHY EACH ONE IS THE PREDICATE IT IS.
 *
 * The canvas names six: All · Money · Records · Roles · Exports · Directory.
 * The backend's `audit_logs.category` enum is a different axis entirely —
 * authentication / patient_access / data_modification / system_config /
 * user_management / compliance — and the first port of this screen mapped one
 * onto the other by position. That produced two false statements on the one
 * surface in the product that must never make one:
 *
 *   - "Money" was `category = 'data_modification'`, which is written by
 *     patients/, medical-records/, voiceprints/, prescription-imports/ and the
 *     recording-session sweep. It means "something was written". Pressing it
 *     returned 203 recording sessions, each stamped `money`.
 *   - "Exports" was `category = 'compliance'`, which only platform-admin/*
 *     controllers write (tier overrides, flags, announcements, AI routing,
 *     payments) — acts a tenant manager never performs. Pressing it returned
 *     eight audit-chain verification failures, each stamped `exports`.
 *
 * So a family is now a predicate over what a row IS — its resource and its
 * action — and `familyOf` below is the same rule the FAMILY column paints
 * with, which is what makes the chip and the column impossible to disagree.
 *
 * THREE OF THE BOARD'S SIX HAVE NO WRITER IN THE BACKEND YET. Nothing in
 * MAIE.Backend/src audits an invoice, an export or a Directory edit, so Money,
 * Exports and Directory are honest, correctly-shaped filters that answer with a
 * designed empty state naming the gap — rather than chips that return somebody
 * else's rows. Each is written up in reports/clinic-manager.md.
 *
 * THE CHIP ROW IS THE BOARD'S SIX, AND ONLY SIX. An earlier port closed the
 * taxonomy by adding "Sign-ins" and "Views" chips, which is not the canvas's
 * row — it pushed "By person ▾" 114px right. The taxonomy closes the other way:
 * a row that is none of the six prints what it ACTUALLY is (`sign-in`,
 * `chart view`, `recording`, `break-glass`, `chain check`, `platform`) in a
 * plain outline chip, and is read from `All`. That is the one thing a family
 * must never do — name a row something it is not — and it is why a recording
 * session opening and timing out no longer arrives stamped `records`.
 */
interface Family {
  key: string;
  label: string;
  /** Up to two server queries, merged newest-first. */
  q: AuditQuery[];
  chip: ChipKind;
  /** What the table says when this family is empty — never a bare default. */
  emptyTitle: string;
  emptySub: string;
}

const FAMILIES: Family[] = [
  {
    key: 'all', label: 'All', q: [{}], chip: 'outline',
    emptyTitle: 'Nothing is logged in this window',
    emptySub: 'Every act in the clinic lands here as it happens, hash-chained and unchangeable.',
  },
  {
    key: 'money', label: 'Money', q: [{ search: 'Invoice' }], chip: 'warn',
    emptyTitle: 'No money event is in the log',
    emptySub: 'A discount, a refund, a void or a corrected bill belongs here. Nothing in the backend audits an invoice yet, so this family has no writer — not a quiet clinic.',
  },
  {
    key: 'records', label: 'Records',
    q: [{ category: 'data_modification' }, { category: 'compliance' }], chip: 'soft',
    emptyTitle: 'Nothing has been written to a record',
    emptySub: 'Addenda, signatures, amendments, discarded drafts, merges and deceased marks all land here.',
  },
  {
    key: 'roles', label: 'Roles',
    q: [{ category: 'user_management' }, { category: 'compliance' }], chip: 'success',
    emptyTitle: 'No staff account has changed',
    emptySub: 'Creating, editing or removing a staff account is written here with who did it.',
  },
  {
    key: 'exports', label: 'Exports', q: [{ search: 'Export' }], chip: 'outline',
    emptyTitle: 'No export is in the log',
    emptySub: 'Exports of reports and of the log itself list here.',
  },
  {
    key: 'directory', label: 'Directory', q: [{ search: 'Directory' }], chip: 'soft',
    emptyTitle: 'No Directory edit is in the log',
    emptySub: 'Proposed and approved edits to labs, imaging centres and suppliers list here.',
  },
];

const MONEY = /invoice|payment|refund|discount|billing|receipt/i;
const EXPORTED = /export/i;
const DIRECTORY = /directory|supplier/i;
const STAFF = /\brole\b|permission|staff account/i;
/** The board's Records family, named by the resources it is actually about. */
const RECORD_RESOURCE = /^(medical record|patient|patient record)$/i;
/* Word-anchored on purpose: a bare /sign/ also matches "assigned", and a
   family that catches a word inside another word is the same class of mistake
   as the catch-all it replaces. */
const RECORD_ACT = /\b(amend\w*|addend\w*|sign\w*|correct\w*|merg\w*|deceased|discard\w*)\b/i;

/**
 * THE ROW'S OWN FAMILY, or none.
 *
 * One rule, used by the FAMILY column and by the chips, so the two can never
 * disagree. It is a PREDICATE OVER WHAT THE ROW IS — its resource and its
 * action — and it is deliberately NOT total: a row that is none of the board's
 * six returns `key: null` and is described by `outsideLabel` below instead of
 * being swept into the nearest family.
 *
 * That last clause is the whole point. The previous version ended
 * `return pick('records')`, and a catch-all is a statement about every row it
 * catches: 21 recording sessions, every break-glass entry, every audit-chain
 * verification failure and every platform-admin act arrived on the manager's
 * screen labelled "records", which is a family the board defines as addenda,
 * signatures, amendments, discarded drafts, merges and deceased marks.
 */
function familyOf(e: AuditLogEntry): { key: string | null; label: string; chip: ChipKind } {
  const resource = (e.resource ?? '').trim();
  const action = (e.action ?? '').trim();
  const text = `${resource} ${action}`;
  const pick = (key: string) => {
    const f = FAMILIES.find((x) => x.key === key) ?? FAMILIES[0];
    return { key: f.key, label: f.label.toLowerCase(), chip: f.chip };
  };
  if (MONEY.test(text)) return pick('money');
  if (EXPORTED.test(text)) return pick('exports');
  if (DIRECTORY.test(text)) return pick('directory');
  /* Roles is the staff account: who exists, what they may do, and the signing
     PIN that stands behind their signature (`resource: 'User'`). */
  if (e.category === 'user_management' || /^user$/i.test(resource) || STAFF.test(text)) {
    return pick('roles');
  }
  /* Records is a WRITE to a chart. Opening one is a read, and reads are not
     this family however the backend files them — which is why a `patient_access`
     row never lands here whatever its resource says. An action-only match has to
     be a write as well, so a future "Signed in" does not arrive as a signature. */
  const writeish = e.category === 'data_modification' || e.category === 'compliance';
  if (e.category !== 'patient_access'
    && (RECORD_RESOURCE.test(resource) || (writeish && RECORD_ACT.test(action)))) {
    return pick('records');
  }
  return { key: null, ...outsideLabel(e) };
}

/**
 * WHAT A ROW OUTSIDE THE SIX IS CALLED. Honest, short, and never one of the
 * family words — the FAMILY column has to be readable on every row of `All`,
 * and "break-glass" in particular is the one line in this log a manager must
 * be able to find, so it carries the warning chip rather than hiding inside a
 * family nobody would open looking for it.
 */
function outsideLabel(e: AuditLogEntry): { label: string; chip: ChipKind } {
  const r = (e.resource ?? '').toLowerCase();
  const a = (e.action ?? '').toLowerCase();
  if (r.includes('emergency') || a.includes('break-glass')) return { label: 'break-glass', chip: 'warn' };
  if (r.includes('audit_chain')) return { label: 'chain check', chip: 'warn' };
  if (r.includes('platform_admin')) return { label: 'platform', chip: 'outline' };
  if (r.includes('recording')) return { label: 'recording', chip: 'outline' };
  if (r.includes('voiceprint')) return { label: 'voiceprint', chip: 'outline' };
  if (r.includes('import')) return { label: 'import', chip: 'outline' };
  if (e.category === 'authentication') return { label: 'sign-in', chip: 'outline' };
  if (e.category === 'patient_access') return { label: 'chart view', chip: 'outline' };
  return { label: 'other', chip: 'outline' };
}

/**
 * WHAT MAY BE PRINTED OUT OF `audit_logs.details`.
 *
 * The column is free-form JSON written by whichever controller raised the row,
 * and iterating the first six keys of it put a tenant UUID, a login username
 * and — in 169 rows of this clinic's log — a PATIENT NAME onto the manager's
 * screen. This whole surface is built on never showing a patient identity, and
 * a future writer adding a field must not be able to break that by accident. So
 * the pane prints an ALLOW-LIST and nothing else: a key that is not here is not
 * shown, whatever it holds.
 */
const DETAIL_LABELS: Record<string, string> = {
  reason: 'Reason typed',
  updatedFields: 'Fields changed',
  rowCount: 'Rows',
  filename: 'File',
  brokenAt: 'Broke at',
};

/**
 * Clinic manager · Audit log — `g_audit.dc.html`.
 *
 * EVERYTHING THAT IS LOGGED IS READABLE HERE. The families across the top, the
 * entry docked beside the list, and the log itself is the source — this screen
 * invents nothing and edits nothing.
 *
 * THE WINDOW IS THE LAST TWO CALENDAR MONTHS, and the sub-line names it. That
 * is not decoration: the sub-line used to be derived from whichever 100 rows
 * came back while the query sent no dates at all, so it described the PAGE
 * rather than the log — "Sep 2026" over "showing 9 of 1,826", and "May – Sep
 * 2026" the moment a chip narrowed the page. Bounding the QUERY makes the line
 * and the total one statement.
 *
 * THE LOG IS HASH-CHAINED in the backend (`rowHash` / `prevHash` / `chainSeq`),
 * which is why nothing here offers to change a row: an audit trail you can edit
 * is not one.
 */
export function ManagerAuditPage() {
  const [family, setFamily] = useState('all');
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  /* NINE ROWS, then "showing 9 of 1,457 · Load more". The board draws nine and
     keeps its own truncation notice above the fold, which is the point of
     §13.3 — a table that states what it is not showing, where you can see it. */
  const [shown, setShown] = useState(9);
  const [personOpen, setPersonOpen] = useState(false);
  const [person, setPerson] = useState<{ id: string; name: string } | null>(null);
  const meId = useAuthStore((s) => s.user?.id);

  /* The window every figure on this screen is true of. Two calendar months,
     ending now — `endDate` is an instant rather than a day so today's rows are
     inside it (the service compares `createdAt BETWEEN`, and a bare
     `YYYY-MM-DD` is UTC midnight). */
  const period = useMemo(() => {
    const now = new Date();
    const from = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const m = (d: Date) => ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][d.getMonth()];
    return {
      startDate: ymd(from),
      endDate: now.toISOString(),
      label: from.getMonth() === now.getMonth()
        ? `${m(now)} ${now.getFullYear()}`
        : `${m(from)} – ${m(now)} ${now.getFullYear()}`,
    };
  }, []);

  const fam = FAMILIES.find((f) => f.key === family) ?? FAMILIES[0];
  /* A family whose predicate IS a search term cannot also carry the typed one —
     `search` is a single ILIKE on the server. Those three narrow client-side
     instead, over a window they already read whole. */
  const familyOwnsSearch = fam.q.some((p) => p.search);
  const typed = query.trim();
  const serverSearch = familyOwnsSearch ? undefined : (typed || undefined);
  const clientSearch = familyOwnsSearch ? typed.toLowerCase() : '';

  const win = { startDate: period.startDate, endDate: period.endDate, limit: 100 };
  const logA = useAuditLog({ ...fam.q[0], search: fam.q[0].search ?? serverSearch, ...win });
  const logB = useAuditLog(
    { ...(fam.q[1] ?? {}), search: fam.q[1]?.search ?? serverSearch, ...win },
    Boolean(fam.q[1]),
  );

  const { providers } = useProviders();

  usePageMeta({ title: 'Audit log' });

  const hasSecond = Boolean(fam.q[1]);
  const fetched = useMemo(() => {
    const rows = hasSecond ? [...logA.page.rows, ...logB.page.rows] : logA.page.rows;
    return [...rows].sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
  }, [hasSecond, logA.page.rows, logB.page.rows]);

  const serverTotal = hasSecond ? logA.page.total + logB.page.total : logA.page.total;

  const entries = useMemo(() => {
    /* The guard that keeps the chip and the FAMILY column honest: a row the
       server handed back whose own family is a different one never shows under
       this chip. */
    let out = family === 'all' ? fetched : fetched.filter((e) => familyOf(e).key === family);
    if (clientSearch) {
      out = out.filter((e) =>
        `${e.action} ${e.resource} ${shortName(e.user)}`.toLowerCase().includes(clientSearch));
    }
    if (person) out = out.filter((e) => e.userId === person.id);
    return out;
  }, [fetched, family, clientSearch, person]);

  /* The server's count is the truth only while nothing was filtered off it. */
  const narrowed = Boolean(person) || Boolean(clientSearch) || entries.length !== fetched.length;
  const total = narrowed ? entries.length : serverTotal;

  const visible = entries.slice(0, shown);
  const selected = selectedId ? entries.find((e) => e.id === selectedId) ?? null : null;

  /** The board's WHO is a person or "You"; a system-generated row is neither. */
  const who = (e: AuditLogEntry) =>
    (e.userId && e.userId === meId ? 'You' : e.user ? shortName(e.user) : 'system');

  const rows: TableRow[] = visible.map((e) => {
    const f = familyOf(e);
    return {
      id: e.id,
      cells: {
        when: <span className="t-sec mut">{stamp(e.createdAt)}</span>,
        who: <span className="t-sec w6">{who(e)}</span>,
        what: (
          <span className="t-sec cm-nowrap">
            {e.action}
            {e.resource ? <span className="mut"> · {e.resource}</span> : null}
          </span>
        ),
        family: <Chip kind={f.chip} small>{f.label}</Chip>,
      },
    };
  });

  const detailRows = selected
    ? Object.entries(selected.details ?? {}).filter(([k]) => k in DETAIL_LABELS)
    : [];

  const paneSig = `${selected?.id ?? ''}`;

  const crumb = usePhoneCrumb('Home', 'Audit log', { search: true });
  useShellChrome(
    {
      raw: true,
      ...crumb,
      /* Phone: the pick is its own screen; back clears it. */
      paneOpen: !!selected,
      onPaneClose: () => setSelectedId(null),
      phoneMenu: (close) => <ManagerMenu door="audit" onClose={close} />,
      title: 'Audit log',
      sub: `${period.label} · all branches`,
      search: 'Search a person, a bill, an MRN, or what happened…',
      searchValue: query,
      onSearchChange: (v) => { setQuery(v); setShown(9); },
      primary: {
        label: 'Export',
        onClick: () =>
          exportCsv(`audit-${family}`, ['When', 'Who', 'What', 'Resource', 'Family', 'Outcome'],
            entries.map((e) => [
              stamp(e.createdAt), who(e), e.action, e.resource,
              familyOf(e).label, e.outcome,
            ])),
      },
      pane: selected ? (
        <Pane
          head={
            <PaneHead
              overline="The selected entry"
              title={selected.action}
              sub="who · what · when · where"
            />
          }
        >
          <Card>
            <p className="t-ovl mut">By · when · where</p>
            <p className="t-sec sec" style={{ marginTop: 4 }}>
              {who(selected)}
              {selected.user?.role?.name ? ` · ${selected.user.role.name}` : ''} ·{' '}
              {stamp(selected.createdAt)}
            </p>
            <p className="t-cap mut">
              {place(selected.ipAddress)}
              {selected.outcome ? ` · ${selected.outcome}` : ''}
            </p>
          </Card>
          <Card>
            <p className="t-ovl mut">The object</p>
            <p className="t-sec sec" style={{ marginTop: 4 }}>
              {selected.resource}
              {selected.resourceId ? ` · ${selected.resourceId.slice(0, 8)}` : ''}
              {' · '}{familyOf(selected).label}
            </p>
            {detailRows.length ? (
              <>
                <p className="t-cap mut">and what was typed at the moment it happened:</p>
                {detailRows.map(([k, v]) => (
                  <p key={k} className="t-sec sec" style={{ marginTop: 4 }}>
                    <span className="mut">{DETAIL_LABELS[k]}</span>{' '}
                    {Array.isArray(v) ? v.join(', ') : String(v)}
                  </p>
                ))}
              </>
            ) : (
              <p className="t-cap mut">
                nothing else was recorded with it that belongs on a manager’s screen
              </p>
            )}
          </Card>
          <p style={{ marginTop: 12 }}>
            <button type="button" className="dchip"
              style={{ border: '1px solid var(--stroke-200)', color: 'var(--ink-600)', background: 'var(--surface-0)' }}
              disabled={!selected.user}
              title={selected.user ? undefined : 'This row was written by the system, not a person'}
              onClick={() => {
                if (!selected.user) return;
                setPerson({ id: selected.userId, name: shortName(selected.user) });
                setSelectedId(null);
                setShown(9);
              }}>
              All by {who(selected)} ›
            </button>
          </p>
        </Pane>
      ) : undefined,
    },
    [family, query, paneSig, person?.id, entries.length, period.label, selectedId],
  );

  return (
    <div className="maie desk">
      <ManagerSurface />
      <div className="dcontent">
        <FilterRow style={{ flexWrap: 'wrap' }}>
          {FAMILIES.map((f) => (
            <Chip key={f.key} kind={family === f.key ? 'brand' : 'outline'} small
              className={family === f.key ? 'w6' : undefined}
              onClick={family === f.key ? undefined : () => {
                setFamily(f.key); setSelectedId(null); setShown(9);
              }}>
              {f.label}
            </Chip>
          ))}
          <span className="t-cap mut" style={{ padding: '0 4px' }}>·</span>
          <Chip kind={person ? 'brand' : 'outline'} small className={person ? 'w6' : undefined}
            onClick={() => setPersonOpen(true)}>
            {person ? `${person.name} ▾` : 'By person ▾'}
          </Chip>
        </FilterRow>

        <DataTable
          caption="Audit log"
          columns={[
            { key: 'when', label: 'When', width: 16 },
            { key: 'who', label: 'Who', width: 14 },
            /* A card leads with what happened; the time and the person stack
               under it. Leading with the timestamp would make every card in
               the log look the same until the second line. */
            { key: 'what', label: 'What', width: 52, role: 'body' },
            { key: 'family', label: 'Family', width: 18 },
          ]}
          rows={rows}
          total={total}
          onLoadMore={shown < entries.length ? () => setShown((n) => n + 25) : undefined}
          selectedId={selectedId}
          onSelect={(r) => setSelectedId((cur) => (cur === r.id ? null : r.id))}
          empty={logA.isLoading || (hasSecond && logB.isLoading)
            ? <EmptyState title="Reading the log…" />
            : <EmptyState title={fam.emptyTitle} sub={fam.emptySub} />}
        />

        <p className="t-sec mut" style={{ marginTop: 12 }}>
          Everything that is logged is readable here — discounts, refunds, addenda, signatures,
          discarded drafts, role and share changes, exports, merges, deceased marks and
          Directory edits.
        </p>
      </div>

      <Modal
        open={personOpen}
        onClose={() => setPersonOpen(false)}
        title="By person"
        sub="narrow the log to one person’s acts"
        width={420}
        foot={<button type="button" className="btn btn-outline" onClick={() => setPersonOpen(false)}>Close</button>}
      >
        <div className="dmenu" style={{ position: 'static', boxShadow: 'none', border: 'none', minWidth: 0, padding: '10px 0 0' }}>
          <button type="button" className="mi" onClick={() => { setPerson(null); setPersonOpen(false); setShown(9); }}>
            <span>Everyone</span>
            {person ? null : <span className="t-cap brand w6">showing</span>}
          </button>
          {peopleIn(entries, providers).map((p) => (
            <button key={p.id} type="button" className="mi"
              onClick={() => { setPerson(p); setPersonOpen(false); setSelectedId(null); setShown(9); }}>
              <span>{p.name}</span>
              {person?.id === p.id ? <span className="t-cap brand w6">showing</span> : null}
            </button>
          ))}
        </div>
      </Modal>
    </div>
  );
}

/**
 * THE "WHERE", AS A PLACE. The board's line is a place — "Maadi · desk PC" —
 * and the backend keeps an IP address, so the address is printed as the one
 * thing it can honestly be read as. A loopback address is not a location: it
 * means the request was made on the machine the server is running on, which is
 * what a desk PC in the clinic is, and "from ::1" is unreadable as a where.
 * Nothing is invented — an address that is not loopback prints as itself.
 */
function place(ip?: string | null): string {
  if (!ip) return 'no address recorded';
  if (ip === '::1' || ip === '127.0.0.1' || ip === '::ffff:127.0.0.1') return 'from this computer';
  return `from ${ip.replace(/^::ffff:/, '')}`;
}

/**
 * Who appears in the log, plus every provider — so the picker offers a person
 * who has done nothing yet rather than hiding them. A system-written row has no
 * person to offer and contributes nobody.
 */
function peopleIn(
  entries: AuditLogEntry[],
  list: Array<{ id: string; firstName: string; lastName: string }>,
): Array<{ id: string; name: string }> {
  const m = new Map<string, string>();
  for (const e of entries) if (e.userId && e.user) m.set(e.userId, shortName(e.user));
  for (const p of list) if (!m.has(p.id)) m.set(p.id, shortName(p));
  return [...m.entries()].map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name));
}
